import authService from './authService';
import {
  ShippingProvider,
  ShippingProviderAddress,
  ShippingProviderFormData,
  ShippingProviderListFilters,
  ShippingProviderListResult,
  ShippingProviderService as ShippingProviderServiceModel,
  ShippingProviderServiceFormData,
} from '../types/shippingProvider';
import { ApiResponse } from '../types/common';

// ─── Service ────────────────────────────────────────────────────

class ShippingProviderServiceApi {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ─── Helpers ──────────────────────────────────────────────────

  private buildListParams(filters?: ShippingProviderListFilters): string {
    const params = new URLSearchParams();
    params.set('page', String(filters?.page ?? 1));
    params.set('page_size', String(filters?.pageSize ?? 20));
    if (filters?.search) params.set('q', filters.search);
    if (filters?.hasAddress) params.set('has_address', 'true');
    return params.toString();
  }

  // ─── Providers (Transportadoras) ─────────────────────────────

  /**
   * Lista paginada — usada no novo CRUD de Transportadoras e em #6 (rotas).
   * Mantém compatibilidade: quando chamada sem filtros, retorna o array bruto
   * (legacy callers em InvoiceDetailView e MaterialReceiptFormView).
   */
  async getProviders(): Promise<ShippingProvider[]>;
  async getProviders(filters: ShippingProviderListFilters): Promise<ShippingProviderListResult>;
  async getProviders(
    filters?: ShippingProviderListFilters,
  ): Promise<ShippingProvider[] | ShippingProviderListResult> {
    const apiClient = await this.getApiClient();

    if (!filters) {
      const res = await apiClient.get('/shipping/providers');
      if (!res.ok) throw new Error('Erro ao buscar provedores de frete');
      const data: ApiResponse<ShippingProvider[] | ShippingProviderListResult> = await res.json();
      // Backend pode retornar lista direta (legacy) ou objeto paginado
      const payload = data.data;
      if (Array.isArray(payload)) return payload;
      if (payload && 'data' in payload) return (payload as ShippingProviderListResult).data;
      return [];
    }

    const qs = this.buildListParams(filters);
    const res = await apiClient.get(`/shipping/providers?${qs}`);
    if (!res.ok) throw new Error('Erro ao buscar provedores de frete');
    const json = await res.json();

    // Formato esperado:
    // { success, data: { data: [...], pagination: { page, page_size, total_items, total_pages } } }
    const payload = json.data ?? json;
    const list: ShippingProvider[] = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    const pag = payload?.pagination ?? {};
    return {
      data: list,
      pagination: {
        page: pag.page ?? filters.page ?? 1,
        pageSize: pag.page_size ?? filters.pageSize ?? 20,
        total: pag.total_items ?? list.length,
        totalPages: pag.total_pages ?? 1,
      },
    };
  }

  async getProviderById(providerId: number): Promise<ShippingProvider> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.get(`/shipping/providers/${providerId}`);
    if (!res.ok) {
      if (res.status === 404) throw new Error('Transportadora não encontrada');
      throw new Error('Erro ao buscar transportadora');
    }
    const data: ApiResponse<ShippingProvider> = await res.json();
    return data.data!;
  }

  async createProvider(payload: ShippingProviderFormData): Promise<ShippingProvider> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post('/shipping/providers', payload);
    if (!res.ok) {
      if (res.status === 409) throw new Error('Já existe uma transportadora com este nome');
      throw new Error('Erro ao criar transportadora');
    }
    const data: ApiResponse<ShippingProvider> = await res.json();
    return data.data!;
  }

  async updateProvider(providerId: number, payload: Partial<ShippingProviderFormData>): Promise<ShippingProvider> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.put(`/shipping/providers/${providerId}`, payload);
    if (!res.ok) {
      if (res.status === 404) throw new Error('Transportadora não encontrada');
      throw new Error('Erro ao atualizar transportadora');
    }
    const data: ApiResponse<ShippingProvider> = await res.json();
    return data.data!;
  }

  /**
   * Endpoint específico para roles que só podem alterar endereço/geo
   * (logistics_operator). Backend valida com permissão `shipping_providers.write_address`.
   */
  async updateProviderAddress(providerId: number, address: ShippingProviderAddress): Promise<ShippingProvider> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.patch(`/shipping/providers/${providerId}/address`, address);
    if (!res.ok) {
      if (res.status === 404) throw new Error('Transportadora não encontrada');
      throw new Error('Erro ao atualizar endereço da transportadora');
    }
    const data: ApiResponse<ShippingProvider> = await res.json();
    return data.data!;
  }

  async deleteProvider(providerId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.delete(`/shipping/providers/${providerId}`);
    if (!res.ok) {
      if (res.status === 404) throw new Error('Transportadora não encontrada');
      if (res.status === 409) throw new Error('Não é possível excluir: transportadora em uso por pedidos/cotações');
      throw new Error('Erro ao excluir transportadora');
    }
  }

  // ─── Provider Services (Serviços de Frete) ───────────────────

  /**
   * Listagem global (legacy). Mantido para compatibilidade com módulos
   * que precisam de todos os serviços (ex: InvoiceDetailView).
   */
  async getProviderServices(): Promise<ShippingProviderServiceModel[]> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.get('/shipping/provider-services');
    if (!res.ok) throw new Error('Erro ao buscar serviços de frete');
    const data: ApiResponse<ShippingProviderServiceModel[]> = await res.json();
    return data.data ?? [];
  }

  /**
   * Lista os serviços de uma transportadora específica (master/detail).
   */
  async getProviderServicesByProvider(providerId: number): Promise<ShippingProviderServiceModel[]> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.get(`/shipping/providers/${providerId}/services`);
    if (!res.ok) throw new Error('Erro ao buscar serviços da transportadora');
    const data: ApiResponse<ShippingProviderServiceModel[]> = await res.json();
    return data.data ?? [];
  }

  async createProviderService(
    providerId: number,
    payload: Omit<ShippingProviderServiceFormData, 'provider_id'>,
  ): Promise<ShippingProviderServiceModel> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post(`/shipping/providers/${providerId}/services`, payload);
    if (!res.ok) {
      if (res.status === 409) throw new Error('Já existe um serviço com este nome para esta transportadora');
      throw new Error('Erro ao criar serviço de frete');
    }
    const data: ApiResponse<ShippingProviderServiceModel> = await res.json();
    return data.data!;
  }

  async updateProviderService(
    providerServiceId: number,
    payload: Partial<ShippingProviderServiceFormData>,
  ): Promise<ShippingProviderServiceModel> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.put(`/shipping/provider-services/${providerServiceId}`, payload);
    if (!res.ok) {
      if (res.status === 404) throw new Error('Serviço de frete não encontrado');
      throw new Error('Erro ao atualizar serviço de frete');
    }
    const data: ApiResponse<ShippingProviderServiceModel> = await res.json();
    return data.data!;
  }

  async deleteProviderService(providerServiceId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.delete(`/shipping/provider-services/${providerServiceId}`);
    if (!res.ok) {
      if (res.status === 404) throw new Error('Serviço de frete não encontrado');
      if (res.status === 409) throw new Error('Não é possível excluir: serviço em uso por cotações');
      throw new Error('Erro ao excluir serviço de frete');
    }
  }
}

// Singleton instance
export const shippingProviderService = new ShippingProviderServiceApi();
export default shippingProviderService;
